import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import { useAuthStore } from '../../stores/authStore';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { ApiError } from '../../services/api';

interface AccountLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Which of the three states the email section renders. */
type EmailView = 'pending' | 'confirmed' | 'form';

const linkButtonStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 8,
  background: 'none',
  border: 'none',
  color: '#8ab4f8',
  cursor: 'pointer',
  padding: 0,
  fontSize: 12,
  textDecoration: 'underline',
};

export const AccountLinkModal: React.FC<AccountLinkModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken, user } = useAuthStore();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Set on a successful send so the pending view appears immediately, without waiting for a refresh.
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Set when the player asks to replace an address that is already pending or confirmed.
  const [editing, setEditing] = useState(false);

  // The modal stays mounted while closed, so transient state has to be cleared on open rather than
  // on unmount. The refresh also picks up a confirmation that was completed in another tab.
  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setStatus(null);
    setError(null);
    setSentTo(null);
    setEditing(false);
    void refreshUser();
  }, [isOpen, refreshUser]);

  if (!isOpen) return null;

  const pendingEmail = sentTo ?? user?.pendingEmail ?? null;
  const confirmedEmail = user?.email ?? null;

  let view: EmailView;
  if (editing) view = 'form';
  else if (pendingEmail) view = 'pending';
  else if (confirmedEmail) view = 'confirmed';
  else view = 'form';

  const handleLinkEmail = async () => {
    setError(null);
    setSending(true);
    setStatus('Sending confirmation email…');
    try {
      const address = email.trim();
      const res = await authService.linkEmail(address);
      setSentTo(res.pendingEmail ?? address);
      setEditing(false);
      setStatus(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to send the confirmation email.');
      setStatus(null);
    } finally {
      setSending(false);
    }
  };

  const startEditing = () => {
    setEmail('');
    setError(null);
    setStatus(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setError(null);
    setStatus(null);
    setEditing(false);
  };

  const startOAuthLink = async (provider: 'discord' | 'google') => {
    try {
      if (!sessionToken) {
        setError('No active session');
        return;
      }
      setStatus('Preparing OAuth link...');
      await authService.setLinkSession(sessionToken);
      // Redirect to provider; callback will link and redirect back
      window.location.href = authService.getOAuthUrl(provider);
    } catch (e) {
      setError('Failed to start OAuth link');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="login-modal text-white">
        <h2 className="text-2xl font-bold mb-4">{user?.isGuest ? 'Upgrade Account' : 'Your Account'}</h2>
        {user?.isGuest && (
          <div style={{ background: '#3b3b3b', padding: 10, borderRadius: 6, marginBottom: 12 }}>
            <strong>Warning:</strong> Guest sessions are temporary. Link an email or OAuth provider to save your progress.
          </div>
        )}

        <div className="auth-section mb-6">
          <h3 className="text-lg font-semibold mb-2">Email Address</h3>

          {view === 'pending' && (
            <div style={{ background: '#2b3b2b', padding: 10, borderRadius: 6, fontSize: 13 }}>
              Check your inbox — we sent a confirmation link to <strong>{pendingEmail}</strong>.
              {user?.isGuest
                ? ' Your account stays a guest account until you click it.'
                : ' Nothing changes on your account until you click it.'}
              <button onClick={startEditing} style={linkButtonStyle}>
                Use a different address
              </button>
            </div>
          )}

          {view === 'confirmed' && (
            <div style={{ background: '#2b3b2b', padding: 10, borderRadius: 6, fontSize: 13 }}>
              <strong>{confirmedEmail}</strong> is confirmed. We'll send sign-in links here if you
              lose access to your other sign-in methods.
              <button onClick={startEditing} style={linkButtonStyle}>
                Use a different address
              </button>
            </div>
          )}

          {view === 'form' && (
            <>
              <p style={{ fontSize: 13, color: '#bbb', marginBottom: 8 }}>
                {confirmedEmail || pendingEmail
                  ? 'Enter the new address. We\'ll send it a confirmation link — your current address stays in place until you click it.'
                  : 'We\'ll send a confirmation link to this address. Clicking it proves the address is yours and lets you sign in with a link from any browser.'}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  style={{ flex: 1, padding: '8px', background: '#222', color: '#fff', borderRadius: 4, border: '1px solid #444' }}
                />
                <button
                  onClick={handleLinkEmail}
                  disabled={sending || email.trim().length === 0}
                  style={{
                    padding: '8px 12px',
                    background: sending || email.trim().length === 0 ? '#3a5a3c' : '#4CAF50',
                    color: sending || email.trim().length === 0 ? '#9a9a9a' : '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: sending || email.trim().length === 0 ? 'default' : 'pointer',
                  }}
                >
                  {sending ? 'Sending…' : 'Send Confirmation'}
                </button>
              </div>
              {(confirmedEmail || pendingEmail) && (
                <button onClick={cancelEditing} style={linkButtonStyle}>
                  Cancel
                </button>
              )}
            </>
          )}
        </div>

        <div className="auth-section mb-6">
          <h3 className="text-lg font-semibold mb-2">Link OAuth</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => startOAuthLink('discord')} style={{ padding: '10px 16px', background: '#5865F2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Link Discord</button>
            <button onClick={() => startOAuthLink('google')} style={{ padding: '10px 16px', background: '#4285F4', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Link Google</button>
          </div>
        </div>

        {error && (
          <div role="alert" style={{ marginTop: 8, padding: 10, borderRadius: 4, border: '1px solid #a33', background: 'rgba(170, 51, 51, 0.15)', color: '#ffb3b3', fontSize: 13 }}>
            {error}
          </div>
        )}
        {status && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>{status}</div>
        )}
      </div>
    </Modal>
  );
}
